import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCTRecommendations } from '../../utils/ctRecommendation';
import IdleTimer from '../shared/elements/idleTimer';
import Product from '../shared/sections/product/index';
import OrderSummary from '../shared/sections/order-summary'
import { logError, setCurrentPage, setDisabledItem, setreorderFailMsg, setReorderProducts,setGlobalThresholdMet, setShowReorderSlider, setViewSimilarFailure } from '../../store/application';
import { RootState } from "../../store";
import { Loader } from '../shared/elements/loader';
import { ReactComponent as VectorIcon } from '../../assets/icons/Vector.svg';
import { useGetCartByCartIdQuery} from '../../store/newCart';
import { useFetchreorderablesMutation } from '../../store/reorder';
import ProductReOrder from './product-reorder';

import '../shopping-bag/index.scss';
import { CartStatus } from '../../enums/cartStatus';
import { Page } from '../../enums/page';
import { LoaderState } from '../../enums/loader';
import { CartOperation } from '../../enums/cartOperation';
import { get as getCookie, delete_cookie } from '../../utils/cookies';
import { datalayerEvent } from '../../utils/analytics/google';
import i18n from '../../utils/translations/i18n';
import { startLoading } from '../../store/application';

import { ShoppingBagSkeleton } from './skeleton';
import { GaPageType } from '../../enums/gaPageType';
import { redirectToURL } from '../../utils/url';
import { config } from '../../utils/config';
import { logSentry } from '../../utils/sentry';
import { EXPRESS_CHECKOUT_COOKIE_NAMES } from '../../enums/checkoutPayments';
import cartActions from '../../store/cartActions';
import { LineItemType } from '../../enums/lineItemType';
import Alert from '@mui/material/Alert';
import { mobileDetectionForWidthLessThan768 } from '../../utils/browser';
import { useGetCartDiscountsQuery, useGetDiscountByDiscountIdQuery } from '../../store/discountCode';
import { calculateThresholdStatus, convertArrayToObject, filterAttributes, filterOutQuantityAndSize } from '../../utils/cart';
import { DispatchErrorsEvent } from '../order-confirmation/ga-utils';

declare const window: any;

const ShoppingBag = (props) => {
    document.title = props.title;
    const dispatch = useDispatch();
    const { t } = i18n;
    dispatch(setCurrentPage(Page.SHOPPINGBAG));
    const {
        applicationState: { user, loadingState, error, currentPage, disabledItem,viewSimilarFailure, redirectCartToHomePage, showWarning, reorderProducts, reorderFailMsg,showReorderSlider },
    } = useSelector((state: RootState) => state);
    const { data: cart, error: getCartError} = useGetCartByCartIdQuery({cartId: user.cartId, userId: user.userId, isLoggedIn: user.isLoggedIn}, {skip : (!user.userId || !user.cartId)});
    const { data: globalDiscountData, refetch:refetchCartDiscounts} = useGetCartDiscountsQuery({cartId: user.cartId,locale: config('locale')}, {skip : (!user.cartId || !cart || cart?.mainItemCount === 0 || cart?.appliedGlobalDiscountId || config('locale') !== 'en-US' )});
    const { data: discountData,refetch: refetchDiscountById} = useGetDiscountByDiscountIdQuery({discountId: cart?.appliedGlobalDiscountId,locale: config('locale')},{ skip: !cart?.appliedGlobalDiscountId || config('locale') !== 'en-US' });
    const [ctState , setCtState] = useState(true);
    const [showCartErrorAlert, setShowCartErrorAlert] = useState(false);
    const [isReorderablesLoading, setIsReorderablesLoading] = useState(false);
    const [fetchreorderablesMutation] = useFetchreorderablesMutation();
    const isMobileDevice = mobileDetectionForWidthLessThan768();
    const recommendationContainer: HTMLElement | null = document.querySelector('.ct-placement-products') as HTMLDivElement;

    const fetchReorderableProductsData = async () => {
        setIsReorderablesLoading(true)
        try {
            const data = {
                "customerId": user?.userId,
                "regionCode": config('regionCode'),
                "locale": config('locale'),
                "lang": config('language'),
                "limit": 8
            };
            const response: any = await fetchreorderablesMutation(data);
            if(response?.data && response?.data.length > 0) {
                dispatch(setReorderProducts(response.data));
            }
        } catch (error) {
            console.error(error);
        } finally{
            setIsReorderablesLoading(false);
        }
    };

    useEffect(() => {
        if(user.isLoggedIn === 'true') {
            // fetch data only if data is not present in redux store
            if(reorderProducts.length === 0 ) {
                fetchReorderableProductsData();
            }
        }
        return () => {
            dispatch(setShowReorderSlider(true));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        if (cart?.isOutOfStockInCart || cart?.hasRemovedSizesinCart) {
            DispatchErrorsEvent(cart?.isOutOfStockInCart ? 'You need to remove out of stock items from your cart' : 'One or more sizes are out of stock and have been removed from this item');
        }
    }, [cart?.isOutOfStockInCart, cart?.hasRemovedSizesinCart]);

    useEffect(()=>{
        // hide reorder slider if its response is delayed than cart and ct-recommendation
        if (((cart && cart?.mainItemCount !== 0) || getCartError) && recommendationContainer?.hasChildNodes() && reorderProducts.length === 0) {
            dispatch(setShowReorderSlider(false));
            if(isReorderablesLoading){
                DispatchErrorsEvent('Could not load Reorder section');
            }
        }        
    },[cart, reorderProducts, ctState, getCartError,recommendationContainer?.hasChildNodes()]);

  // Function to check if two attribute objects are equal
  const areAttributesEqual = (attributes1, attributes2) => {
    const filteredArray = convertArrayToObject(filterOutQuantityAndSize(attributes1));
    return JSON.stringify(filterAttributes(filteredArray)) === JSON.stringify(filterAttributes(attributes2));
  };

    useEffect(()=>{
        // to remove products in slider which are present in cart
        if (reorderProducts?.length !== 0) {
            const updatedProducts = reorderProducts?.map(product => {
                // Determine if the product is present in the cart
                const isProductPresent = Object.values(cart?.lineItems || {}).some(items => {
                    if (!Array.isArray(items)) return false;
                
                    const lineItem = items[0]?.data;
                    if (!lineItem || lineItem.productKey !== product.productKey) return false;

                    if (!lineItem?.customfield?.isMultiSelectable) {
                    // Directly check and assign variantId
                    return lineItem.variantId === product.variantId;
                    }
                
                    // Compare attributes if the product is multi-selectable
                    return areAttributesEqual(lineItem.userSelection, product?.selectionAttributes);
                });

                // Create and return a new product object with the updated isPresentInCart property
                return { ...product, isPresentInCart: Boolean(isProductPresent) };
            });

            if(cart?.lineItems && cart?.mainItemCount){
                dispatch(setReorderProducts(updatedProducts));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[cart?.lineItems, cart?.mainItemCount, reorderProducts?.length]);

    useEffect(() => {
        if(reorderFailMsg?.errorMsg.length > 0 && reorderFailMsg?.lineItemId && reorderProducts?.length > 0) {
            setShowCartErrorAlert(true);
            dispatch(setreorderFailMsg({errorMsg: '', lineItemId: ''}))
            setTimeout(() => {setShowCartErrorAlert(false)}, 5000);
        }
        if(viewSimilarFailure) {
            setShowCartErrorAlert(true);
            dispatch(setViewSimilarFailure(false));
            setTimeout(() => {setShowCartErrorAlert(false)}, 3000);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[reorderFailMsg,viewSimilarFailure]);

    useEffect(() => {
        if(getCartError && redirectCartToHomePage){
            try {
                const errorObj = {cartId: user.cartId,userId: user.userId, isLoggedIn: user.isLoggedIn,error:getCartError}
                const obj = {
                    'page': 'shoppingBag',   
                    'feature': 'redirectToHomePageFromShoppingBag on recalculate failure',
                    'operation': 'redirect to HomePage on recalculate failure',
                    'errorType':'recalculate API error',
                    'err': JSON.stringify(errorObj),
                    'func': 'getCartByCartId',
                    'api':'v1/carts/:cartId/recalculate',
                    'errMsg':'redirectToHomePageFromShoppingBag on recalculate failure',
                }
                console.log("redirectToHomePageFromShoppingBag on recalculate failure",JSON.stringify(obj))
                logSentry(obj)    
                redirectToURL(`/${config('locale_details').current_sitename}/`);
            }
            catch(e) {
                console.log("Error while logging getCartError from shoppingBag",e);
            }
        }
        else if (getCartError && ctState){
            getCTRecommendations({}, 'Cart Page', 'shopping-cart');
            setCtState(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getCartError,redirectCartToHomePage]);

    useEffect(() => {
        const cartIdCookie = getCookie('np.cartId');
        if(cart?.id && cart?.mainItemCount === 0){
            delete_cookie('np.brandKeywordCampaign');
        }
        if (cart?.id && ((cart?.mainItemCount > 0 && ctState) || (cart?.mainItemCount == 0)) && !cart?.hasPCCItem) {
            getCTRecommendations(cart, 'Cart Page', 'shopping-cart');
            setCtState(false);
        }
        else if ((!cartIdCookie || cartIdCookie == '') && !cart) {
            getCTRecommendations({}, 'Cart Page', 'shopping-cart');
            setCtState(false);
        }
        const ctRecommendations: HTMLElement | null = document.querySelector("#shopping-bag-ct-recs") as HTMLElement;
        if(cart?.hasPCCItem) {
            ctRecommendations.style.display = 'none';
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.mainItemCount]);

    useEffect(() => {
        if (globalDiscountData?.id && cart) {    
            // Calculate the threshold status
            const { isThresholdMet } = calculateThresholdStatus(cart, globalDiscountData?.cartSubTotal);
    
            // Sets a flag indicating whether threshold is met to show global discount message
            dispatch(setGlobalThresholdMet(isThresholdMet));
        }
    }, [globalDiscountData, cart?.cartSubtotal]); 

    useEffect(() => {
        if(cart?.mainItemCount > 0){
            if(cart?.appliedGlobalDiscountId){
                refetchDiscountById()
            }else{
                refetchCartDiscounts()
            }
        }
    },[cart?.appliedGlobalDiscountId,cart?.mainItemCount])


    useEffect(() => {
        delete_cookie(EXPRESS_CHECKOUT_COOKIE_NAMES.EXPRESS_CHECKOUT);
        delete_cookie(EXPRESS_CHECKOUT_COOKIE_NAMES.APPLE_PAY_EXPRESS_CHECKOUT);
        const headerRoot: any = document.querySelector("[id=cart-header]");
        if(headerRoot) headerRoot.style.display = "block"

        const shippingHeader: any = document.querySelector("[id=shipping-header]");  
        if(shippingHeader) shippingHeader.style.display = "none" 
        
        datalayerEvent(GaPageType.SHOPPINGBAG, cart?.id, ''); 
        const ctRecommendations : any = document.querySelector("#shopping-bag-ct-recs");
        if(ctRecommendations && !cart?.hasPCCItem) {
            ctRecommendations.style.display = 'block';
        }
        // Check if the orderId/CartId is present in sessionStorage and remove it
        if (sessionStorage.getItem("orderConfirmationIds")) {
            sessionStorage.removeItem("orderConfirmationIds");
        }         
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (user?.cartId === '') {
            dispatch(logError(true, CartOperation.GETCART, null));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.cartId]);
      
    useEffect(() => {
        if (cart?.disabledItemInfo?.containsDisabledItem) {
            dispatch(setDisabledItem(true));
            (Object.keys(cart?.lineItems).map((lineItemId:string) => {
                cart?.disabledItemInfo?.lineItemIdArray.map((itemId: string)=>{
                    if(lineItemId === itemId) {
                        removeDisabledLineItem(itemId);
                    }
                });
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.disabledItemInfo?.containsDisabledItem]);

    useEffect(() => {
        if(!error?.isError && cart?.id){
            if(window?.np?.cartPageLoadTimeSentry){
                window?.np?.cartPageLoadTimeSentry.finish();
            }
        }
    },[error?.isError,cart?.id])

    const removeDisabledLineItem = (lineItemId) => {
        dispatch(startLoading());
        dispatch(cartActions.removeLineItem(lineItemId, LineItemType.MAINITEM, cart, lineItemId))
    }

    const renderShoppingBagComponent = () => {
        return (
            <>
                <IdleTimer />
                <Loader isVisible={loadingState === LoaderState.START} />

                { (error?.isError && error?.errorObj.operation === CartOperation.GETCART && (cart?.mainItemCount === 0 || !cart) || (!props.cartId || (cart?.cartState === CartStatus.ACTIVE && cart?.mainItemCount === 0))) ?
                    <div className="slab slab--reverse">
                        <div className="shopping-bag" id="shopping-bag">
                            <div className="row">
                                <div className="col-sm-6 col-md-7 col-lg-8">
                                    <div className="padded-right title-underline">
                                        <h1 className="checkout-title">
                                            {t('shopping_cart_details.pageTitle')}&nbsp;<span id="mainitem-count" className="weight-normal">({cart?.mainItemCount ?? 0})</span>
                                        </h1>
                                    </div>
                                </div>
                            </div>
                            <div className= "zero-product">
                                <h1>{`${t('shopping_cart_details.empty_cart1')} ${cart?.mainItemCount ?? 0} ${t('shopping_cart_details.empty_cart2')}`}</h1>
                                {disabledItem &&
                                    <div id="disabled-product-msg"><VectorIcon/>{t('shopping_cart_details.disabled_product_msg')}</div>
                                }
                            </div>
                        </div>
                    </div>
                : 
                    <div className="slab slab--reverse">
                        <div id="shopping-bag" className="shopping-bag">
                            <div className="shopping-bag_container" id="cart-custom-id">
                                <div className="shopping-bag-products">
                                    <div className='padded-right title-underline'>
                                        <h1 className="checkout-title">
                                            {t('shopping_cart_details.pageTitle')}&nbsp;<span id="mainitem-count" className="weight-normal">({cart?.mainItemCount ?? 0})</span>
                                        </h1>
                                        {disabledItem &&
                                            <div id="disabled-product-msg"><VectorIcon/>{t('shopping_cart_details.disabled_product_msg')}</div>
                                        }
                                        {showWarning && !disabledItem &&
                                            <div id="disabled-product-msg"><VectorIcon/>{t('error.somethingwentwrong')}</div>
                                        }
                                    </div>
                                    {cart?.cartState === CartStatus.ACTIVE && cart?.mainItemCount !== 0 && Object.keys(cart?.lineItems).map((lineItemId) => <Product key={lineItemId} lineItemId={lineItemId} cart={cart} user={user} error={error}/>)}
                                    <div className='reorder-wrapper'>
                                        {(user?.isLoggedIn === 'true') && showReorderSlider && (reorderProducts.length > 0) && (!cart?.hasPCCItem) && <ProductReOrder reorderableProducts={reorderProducts} cart={cart}/>}
                                    </div>
                                </div>
                                {Object.keys(cart?.lineItems).length > 0 &&
                                    <div className="order-summary-shopping-bag">
                                        <OrderSummary cart={cart} user={user} error={error} globalDiscountData={!cart?.appliedGlobalDiscountId ? globalDiscountData : discountData}/>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
                {((error?.isError && error?.errorObj.operation === CartOperation.GETCART) || (cart?.mainItemCount === 0)) && (user?.isLoggedIn === 'true') && showReorderSlider && (reorderProducts.length > 0) && (!cart?.hasPCCItem) && <ProductReOrder reorderableProducts={reorderProducts} cart={cart}/>}
                {showCartErrorAlert && <Alert className="reorder-error-pop-up" severity="error" onClose={() => {setShowCartErrorAlert(false)}}>{t('sign_up.somethingWrong')}</Alert>}
            </>
        );
    }

    return (
        <>
            { !error?.isError && !cart?.id  ?
                <ShoppingBagSkeleton /> : renderShoppingBagComponent()
            }     
        </>
    )
}

export default ShoppingBag;